export type BBox = { x: number; y: number; w: number; h: number }

export type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w"

type Point = { x: number; y: number }

type StrokeLike = {
  tool: "pen" | "eraser"
  width: number
  points: Point[]
}

type TextLike = {
  x: number
  y: number
  text: string
  fontSize: number
  fontFamily?: string
  bold?: boolean
  italic?: boolean
}

type ArrowLike = {
  from: Point
  to: Point
  width: number
  label?: string | null
  labelFontSize?: number
  labelBold?: boolean
  labelItalic?: boolean
}

type ShapeLike = {
  kind: "rect" | "ellipse"
  x: number
  y: number
  w: number
  h: number
  width: number
}

const TEXT_LINE_HEIGHT = 1.2
const DEFAULT_ARROW_LABEL_SIZE = 16

export function strokeBBox(stroke: StrokeLike): BBox {
  const pad = stroke.width / 2 + 2
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const p of stroke.points) {
    minX = Math.min(minX, p.x)
    minY = Math.min(minY, p.y)
    maxX = Math.max(maxX, p.x)
    maxY = Math.max(maxY, p.y)
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 }
  return {
    x: minX - pad,
    y: minY - pad,
    w: Math.max(1, maxX - minX + pad * 2),
    h: Math.max(1, maxY - minY + pad * 2),
  }
}

let measureCtx: CanvasRenderingContext2D | null = null

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null
  if (!measureCtx) {
    const canvas = document.createElement("canvas")
    measureCtx = canvas.getContext("2d")
  }
  return measureCtx
}

function textFontCss(t: TextLike): string {
  const style = t.italic ? "italic" : "normal"
  const weight = t.bold ? "700" : "400"
  const family = t.fontFamily ?? "system-ui, sans-serif"
  return `${style} ${weight} ${t.fontSize}px ${family}`
}

export function textBBox(t: TextLike): BBox {
  const lines = (t.text || " ").split("\n")
  const linePx = t.fontSize * TEXT_LINE_HEIGHT
  const h = Math.max(linePx, lines.length * linePx)
  const ctx = getMeasureCtx()
  let maxW = Math.max(8, t.fontSize * 0.35)
  if (ctx) {
    ctx.font = textFontCss(t)
    for (const line of lines) {
      maxW = Math.max(maxW, ctx.measureText(line.length > 0 ? line : " ").width)
    }
  } else {
    maxW = Math.max(maxW, ...lines.map((line) => Math.max(t.fontSize * 0.35, (line.length || 1) * t.fontSize * 0.52)))
  }
  return { x: t.x, y: t.y, w: Math.ceil(maxW) + 2, h }
}

export function arrowLabelOrigin(a: ArrowLike): Point {
  const mx = (a.from.x + a.to.x) / 2
  const my = (a.from.y + a.to.y) / 2
  const dx = a.to.x - a.from.x
  const dy = a.to.y - a.from.y
  const len = Math.hypot(dx, dy) || 1
  const ox = (-dy / len) * 14
  const oy = (dx / len) * 14
  return { x: mx + ox, y: my + oy }
}

export function arrowLabelFontSize(a: ArrowLike): number {
  return a.labelFontSize ?? DEFAULT_ARROW_LABEL_SIZE
}

export function arrowLabelBBox(a: ArrowLike): BBox | null {
  if (a.label == null) return null
  const origin = arrowLabelOrigin(a)
  const fontSize = arrowLabelFontSize(a)
  return textBBox({
    x: origin.x,
    y: origin.y,
    text: a.label || " ",
    fontSize,
    bold: a.labelBold,
    italic: a.labelItalic,
  })
}

export function arrowBBox(a: ArrowLike): BBox {
  const pad = Math.max(8, a.width * 2 + 4)
  let minX = Math.min(a.from.x, a.to.x) - pad
  let minY = Math.min(a.from.y, a.to.y) - pad
  let maxX = Math.max(a.from.x, a.to.x) + pad
  let maxY = Math.max(a.from.y, a.to.y) + pad
  const labelBox = arrowLabelBBox(a)
  if (labelBox) {
    minX = Math.min(minX, labelBox.x)
    minY = Math.min(minY, labelBox.y)
    maxX = Math.max(maxX, labelBox.x + labelBox.w)
    maxY = Math.max(maxY, labelBox.y + labelBox.h)
  }
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) }
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * dx + (py - ay) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

function circleIntersectsBBox(cx: number, cy: number, radius: number, b: BBox): boolean {
  const nearestX = Math.max(b.x, Math.min(cx, b.x + b.w))
  const nearestY = Math.max(b.y, Math.min(cy, b.y + b.h))
  return Math.hypot(cx - nearestX, cy - nearestY) <= radius
}

function strokeHitsRadius(s: StrokeLike, x: number, y: number, radius: number): boolean {
  if (s.tool === "eraser") return false
  const threshold = radius + s.width / 2
  if (s.points.length === 0) return false
  if (s.points.length === 1) {
    const p = s.points[0]
    return Math.hypot(x - p.x, y - p.y) <= threshold
  }
  for (let j = 1; j < s.points.length; j++) {
    const a = s.points[j - 1]
    const b = s.points[j]
    if (distToSegment(x, y, a.x, a.y, b.x, b.y) <= threshold) return true
  }
  return false
}

export function hitTestStroke<T extends StrokeLike & { id: string }>(strokes: T[], x: number, y: number): T | null {
  for (let i = strokes.length - 1; i >= 0; i--) {
    const s = strokes[i]
    if (s.tool === "eraser") continue
    const threshold = Math.max(6, s.width / 2 + 4)
    if (s.points.length === 1) {
      const p = s.points[0]
      if (Math.hypot(x - p.x, y - p.y) <= threshold) return s
      continue
    }
    for (let j = 1; j < s.points.length; j++) {
      const a = s.points[j - 1]
      const b = s.points[j]
      if (distToSegment(x, y, a.x, a.y, b.x, b.y) <= threshold) return s
    }
  }
  return null
}

/** All pen strokes intersecting eraser brush circle (radius = half brush width in canvas px). */
export function collectStrokesInRadius<T extends StrokeLike & { id: string }>(
  strokes: T[],
  x: number,
  y: number,
  radius: number,
): T[] {
  const hit: T[] = []
  for (const s of strokes) {
    if (strokeHitsRadius(s, x, y, radius)) hit.push(s)
  }
  return hit
}

export function hitTestText<T extends TextLike & { id: string }>(texts: T[], x: number, y: number): T | null {
  for (let i = texts.length - 1; i >= 0; i--) {
    const t = texts[i]
    const b = textBBox(t)
    if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return t
  }
  return null
}

export function collectTextsInRadius<T extends TextLike & { id: string }>(
  texts: T[],
  x: number,
  y: number,
  radius: number,
): T[] {
  const hit: T[] = []
  for (const t of texts) {
    if (circleIntersectsBBox(x, y, radius, textBBox(t))) hit.push(t)
  }
  return hit
}

export function hitTestArrowLabel<T extends ArrowLike & { id: string }>(arrows: T[], x: number, y: number): T | null {
  for (let i = arrows.length - 1; i >= 0; i--) {
    const a = arrows[i]
    const box = arrowLabelBBox(a)
    if (!box) continue
    if (x >= box.x && x <= box.x + box.w && y >= box.y && y <= box.y + box.h) return a
  }
  return null
}

export function hitTestArrow<T extends ArrowLike & { id: string }>(arrows: T[], x: number, y: number): T | null {
  const onLabel = hitTestArrowLabel(arrows, x, y)
  if (onLabel) return onLabel
  for (let i = arrows.length - 1; i >= 0; i--) {
    const a = arrows[i]
    const threshold = Math.max(8, a.width / 2 + 6)
    if (distToSegment(x, y, a.from.x, a.from.y, a.to.x, a.to.y) <= threshold) return a
  }
  return null
}

export function collectArrowsInRadius<T extends ArrowLike & { id: string }>(
  arrows: T[],
  x: number,
  y: number,
  radius: number,
): T[] {
  const hit: T[] = []
  for (const a of arrows) {
    const labelBox = arrowLabelBBox(a)
    if (labelBox && circleIntersectsBBox(x, y, radius, labelBox)) {
      hit.push(a)
      continue
    }
    const threshold = radius + a.width / 2
    if (distToSegment(x, y, a.from.x, a.from.y, a.to.x, a.to.y) <= threshold) {
      hit.push(a)
    }
  }
  return hit
}

export function translateStroke<T extends StrokeLike>(stroke: T, dx: number, dy: number): T {
  return {
    ...stroke,
    points: stroke.points.map((p) => ({ x: p.x + dx, y: p.y + dy })),
  }
}

export function translateText<T extends TextLike>(t: T, dx: number, dy: number): T {
  return { ...t, x: t.x + dx, y: t.y + dy }
}

export function translateArrow<T extends ArrowLike>(a: T, dx: number, dy: number): T {
  return {
    ...a,
    from: { x: a.from.x + dx, y: a.from.y + dy },
    to: { x: a.to.x + dx, y: a.to.y + dy },
  }
}

export function shapeBBox(s: ShapeLike): BBox {
  const pad = s.width / 2 + 2
  return {
    x: s.x - pad,
    y: s.y - pad,
    w: Math.max(1, s.w + pad * 2),
    h: Math.max(1, s.h + pad * 2),
  }
}

function distToRectStroke(px: number, py: number, x: number, y: number, w: number, h: number): number {
  const x1 = x + w
  const y1 = y + h
  return Math.min(
    distToSegment(px, py, x, y, x1, y),
    distToSegment(px, py, x1, y, x1, y1),
    distToSegment(px, py, x1, y1, x, y1),
    distToSegment(px, py, x, y1, x, y),
  )
}

function distToEllipseStroke(px: number, py: number, x: number, y: number, w: number, h: number): number {
  const cx = x + w / 2
  const cy = y + h / 2
  const rx = Math.max(1, w / 2)
  const ry = Math.max(1, h / 2)
  const nx = (px - cx) / rx
  const ny = (py - cy) / ry
  const r = Math.hypot(nx, ny)
  if (r < 1e-6) return Math.min(rx, ry)
  const ex = cx + (nx / r) * rx
  const ey = cy + (ny / r) * ry
  return Math.hypot(px - ex, py - ey)
}

function shapeStrokeDistance(s: ShapeLike, x: number, y: number): number {
  if (s.kind === "ellipse") return distToEllipseStroke(x, y, s.x, s.y, s.w, s.h)
  return distToRectStroke(x, y, s.x, s.y, s.w, s.h)
}

export function hitTestShape<T extends ShapeLike & { id: string }>(shapes: T[], x: number, y: number): T | null {
  for (let i = shapes.length - 1; i >= 0; i--) {
    const s = shapes[i]
    const threshold = Math.max(6, s.width / 2 + 4)
    if (shapeStrokeDistance(s, x, y) <= threshold) return s
  }
  return null
}

export function translateShape<T extends ShapeLike>(s: T, dx: number, dy: number): T {
  return { ...s, x: s.x + dx, y: s.y + dy }
}

export function transformShapeToBBox<T extends ShapeLike>(s: T, from: BBox, to: BBox): T {
  const sx = from.w > 0 ? to.w / from.w : 1
  const sy = from.h > 0 ? to.h / from.h : 1
  const p0 = mapPointInBBox({ x: s.x, y: s.y }, from, to)
  const p1 = mapPointInBBox({ x: s.x + s.w, y: s.y + s.h }, from, to)
  return {
    ...s,
    x: Math.min(p0.x, p1.x),
    y: Math.min(p0.y, p1.y),
    w: Math.max(1, Math.abs(p1.x - p0.x)),
    h: Math.max(1, Math.abs(p1.y - p0.y)),
    width: Math.max(1, s.width * ((sx + sy) / 2)),
  }
}

function mapPointInBBox(p: Point, from: BBox, to: BBox): Point {
  const nx = from.w > 0 ? (p.x - from.x) / from.w : 0
  const ny = from.h > 0 ? (p.y - from.y) / from.h : 0
  return { x: to.x + nx * to.w, y: to.y + ny * to.h }
}

export function resizeBBox(bbox: BBox, handle: ResizeHandle, dx: number, dy: number): BBox {
  let { x, y, w, h } = bbox
  const minSize = 8
  switch (handle) {
    case "nw":
      x += dx
      y += dy
      w -= dx
      h -= dy
      break
    case "n":
      y += dy
      h -= dy
      break
    case "ne":
      y += dy
      w += dx
      h -= dy
      break
    case "e":
      w += dx
      break
    case "se":
      w += dx
      h += dy
      break
    case "s":
      h += dy
      break
    case "sw":
      x += dx
      w -= dx
      h += dy
      break
    case "w":
      x += dx
      w -= dx
      break
  }
  if (w < minSize) {
    if (handle.includes("w")) x = bbox.x + bbox.w - minSize
    w = minSize
  }
  if (h < minSize) {
    if (handle.includes("n")) y = bbox.y + bbox.h - minSize
    h = minSize
  }
  return { x, y, w, h }
}

export function transformStrokeToBBox<T extends StrokeLike>(stroke: T, from: BBox, to: BBox): T {
  const sx = from.w > 0 ? to.w / from.w : 1
  const sy = from.h > 0 ? to.h / from.h : 1
  return {
    ...stroke,
    width: Math.max(1, stroke.width * ((sx + sy) / 2)),
    points: stroke.points.map((p) => mapPointInBBox(p, from, to)),
  }
}

export function transformTextToBBox<T extends TextLike>(t: T, from: BBox, to: BBox): T {
  const sx = from.w > 0 ? to.w / from.w : 1
  const sy = from.h > 0 ? to.h / from.h : 1
  const scale = (sx + sy) / 2
  const old = textBBox(t)
  const nx = from.w > 0 ? (old.x - from.x) / from.w : 0
  const ny = from.h > 0 ? (old.y - from.y) / from.h : 0
  const newBox = { x: to.x + nx * to.w, y: to.y + ny * to.h, w: old.w * sx, h: old.h * sy }
  return {
    ...t,
    x: newBox.x,
    y: newBox.y,
    fontSize: Math.max(8, Math.round(t.fontSize * scale)),
  }
}

export function transformArrowToBBox<T extends ArrowLike>(a: T, from: BBox, to: BBox): T {
  const sx = from.w > 0 ? to.w / from.w : 1
  const sy = from.h > 0 ? to.h / from.h : 1
  const scale = (sx + sy) / 2
  return {
    ...a,
    from: mapPointInBBox(a.from, from, to),
    to: mapPointInBBox(a.to, from, to),
    width: Math.max(1, a.width * scale),
    labelFontSize: Math.max(8, Math.round(arrowLabelFontSize(a) * scale)),
  }
}

export function unionBBox(boxes: BBox[]): BBox | null {
  if (boxes.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const b of boxes) {
    if (b.w < 1 && b.h < 1) continue
    minX = Math.min(minX, b.x)
    minY = Math.min(minY, b.y)
    maxX = Math.max(maxX, b.x + b.w)
    maxY = Math.max(maxY, b.y + b.h)
  }
  if (!Number.isFinite(minX)) return null
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) }
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

/** Clip pen strokes by eraser brush circle; split into remaining polylines with new ids. */
export function eraseStrokesAt<T extends StrokeLike & { id: string }>(
  strokes: T[],
  x: number,
  y: number,
  radius: number,
  nextId: () => string = newStrokeId,
): T[] {
  const out: T[] = []
  for (const s of strokes) {
    if (s.tool !== "pen") continue
    const threshold = radius + s.width / 2
    if (s.points.length === 0) continue
    if (!strokeHitsRadius(s, x, y, radius)) {
      out.push(s)
      continue
    }

    const runs: Point[][] = []
    let run: Point[] = []
    for (const p of s.points) {
      if (Math.hypot(p.x - x, p.y - y) <= threshold) {
        if (run.length > 0) {
          runs.push(run)
          run = []
        }
      } else {
        run.push({ x: p.x, y: p.y })
      }
    }
    if (run.length > 0) runs.push(run)

    if (runs.length === 0) continue
    if (runs.length === 1 && runs[0].length === s.points.length) {
      out.push(s)
      continue
    }
    for (const points of runs) {
      out.push({ ...s, id: nextId(), points })
    }
  }
  return out
}

export function normalizeRect(x0: number, y0: number, x1: number, y1: number): BBox {
  const x = Math.min(x0, x1)
  const y = Math.min(y0, y1)
  return { x, y, w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) }
}

export function newStrokeId(): string {
  return `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function newArrowId(): string {
  return `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function newShapeId(): string {
  return `sh-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function pointInBBox(bbox: BBox, x: number, y: number): boolean {
  return x >= bbox.x && x <= bbox.x + bbox.w && y >= bbox.y && y <= bbox.y + bbox.h
}

export type PenStrokeKind = "marker" | "highlighter"

function distPointToSegmentSq(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq < 1e-12) {
    const ex = p.x - a.x
    const ey = p.y - a.y
    return ex * ex + ey * ey
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  const ex = p.x - cx
  const ey = p.y - cy
  return ex * ex + ey * ey
}

/** Douglas–Peucker polyline simplification. */
export function simplifyPolyline(points: Point[], epsilon: number): Point[] {
  if (points.length < 3) return points.map((p) => ({ x: p.x, y: p.y }))
  const epsSq = epsilon * epsilon

  const keep = new Uint8Array(points.length)
  keep[0] = 1
  keep[points.length - 1] = 1

  const stack: Array<{ start: number; end: number }> = [{ start: 0, end: points.length - 1 }]
  while (stack.length > 0) {
    const { start, end } = stack.pop()!
    if (end - start < 2) continue
    let maxDist = 0
    let maxIdx = start
    const a = points[start]
    const b = points[end]
    for (let i = start + 1; i < end; i++) {
      const d = distPointToSegmentSq(points[i], a, b)
      if (d > maxDist) {
        maxDist = d
        maxIdx = i
      }
    }
    if (maxDist > epsSq) {
      keep[maxIdx] = 1
      stack.push({ start, end: maxIdx }, { start: maxIdx, end })
    }
  }

  const out: Point[] = []
  for (let i = 0; i < points.length; i++) {
    if (keep[i]) out.push({ x: points[i].x, y: points[i].y })
  }
  return out
}

/** Chaikin corner-cutting smoother. */
export function chaikinSmooth(points: Point[], iterations: number): Point[] {
  if (points.length < 3 || iterations <= 0) return points.map((p) => ({ x: p.x, y: p.y }))
  let cur = points.map((p) => ({ x: p.x, y: p.y }))
  for (let n = 0; n < iterations; n++) {
    if (cur.length < 3) break
    const next: Point[] = [{ x: cur[0].x, y: cur[0].y }]
    for (let i = 0; i < cur.length - 1; i++) {
      const a = cur[i]
      const b = cur[i + 1]
      next.push(
        { x: 0.75 * a.x + 0.25 * b.x, y: 0.75 * a.y + 0.25 * b.y },
        { x: 0.25 * a.x + 0.75 * b.x, y: 0.25 * a.y + 0.75 * b.y },
      )
    }
    next.push({ x: cur[cur.length - 1].x, y: cur[cur.length - 1].y })
    cur = next
  }
  return cur
}

/** Simplify + smooth pen centerline after pointerup. */
export function finalizePenStrokePoints(points: Point[], penKind: PenStrokeKind = "marker"): Point[] {
  if (points.length < 3) return points.map((p) => ({ x: p.x, y: p.y }))
  if (penKind === "highlighter") {
    return chaikinSmooth(simplifyPolyline(points, 1.6), 1)
  }
  return chaikinSmooth(simplifyPolyline(points, 1.2), 2)
}

/**
 * Closed polygon for a constant-width ribbon (butt caps).
 * Offsets are perpendicular to local path tangent.
 */
export function strokeRibbonPolygon(points: Point[], halfWidth: number): Point[] {
  if (points.length < 2 || halfWidth <= 0) return []

  const left: Point[] = []
  const right: Point[] = []

  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]
    const next = points[Math.min(points.length - 1, i + 1)]
    let dx = next.x - prev.x
    let dy = next.y - prev.y
    const len = Math.hypot(dx, dy)
    if (len < 1e-6) {
      dx = 1
      dy = 0
    } else {
      dx /= len
      dy /= len
    }
    const nx = -dy * halfWidth
    const ny = dx * halfWidth
    const p = points[i]
    left.push({ x: p.x + nx, y: p.y + ny })
    right.push({ x: p.x - nx, y: p.y - ny })
  }

  return [...left, ...right.reverse()]
}

/** Corners of a flat tip rectangle. `angle` is travel direction (radians); long axis is perpendicular. */
export function orientedTipRect(cx: number, cy: number, angle: number, length: number, thickness: number): Point[] {
  const halfL = length / 2
  const halfT = thickness / 2
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  // Local: x along travel (thin), y across (long tip)
  const corners = [
    { x: -halfT, y: -halfL },
    { x: halfT, y: -halfL },
    { x: halfT, y: halfL },
    { x: -halfT, y: halfL },
  ]
  return corners.map((c) => ({
    x: cx + c.x * cos - c.y * sin,
    y: cy + c.x * sin + c.y * cos,
  }))
}

export function tipThicknessForWidth(width: number): number {
  return Math.max(1.5, width * 0.08)
}

export function pathTravelAngle(points: Point[], fallback = Math.PI / 4): number {
  for (let i = points.length - 1; i > 0; i--) {
    const a = points[i - 1]
    const b = points[i]
    const dx = b.x - a.x
    const dy = b.y - a.y
    if (Math.hypot(dx, dy) >= 1e-3) return Math.atan2(dy, dx)
  }
  return fallback
}
